// src/services/programWaitlist.js
// Firestore helpers for program-level waitlists
// Collection: programWaitlists
// Doc ID: {userId}_{programId}
// Schema: { userId, userEmail, programId, programName, joinedAt }
//
// This is separate from the pod-level waitlist (stored in pods.waitlist array).
// This waitlist is for when a program has NO available pods at all —
// users express interest and Danielle can see who wants the next round.

import { db, firebaseEnabled } from './firebase';
import {
    collection, doc, getDoc, getDocs, query, where, orderBy,
} from 'firebase/firestore';
// Writes wait for the member's Firestore identity (adr-firestore-identity.md a2).
import {
    setDoc, deleteDoc,
} from './firestoreWrite';

// ── Join a program waitlist ──
export async function joinProgramWaitlist(userId, userEmail, programId, programName) {
    if (!firebaseEnabled || !db || !userId || !programId) return false;
    try {
        const docId = `${userId}_${programId}`;
        await setDoc(doc(db, 'programWaitlists', docId), {
            userId,
            userEmail: userEmail || '',
            programId,
            programName: programName || '',
            joinedAt: new Date().toISOString(),
        });
        return true;
    } catch (e) { console.error('Error joining program waitlist:', e); return false; }
}

// ── Leave a program waitlist ──
export async function leaveProgramWaitlist(userId, programId) {
    if (!firebaseEnabled || !db || !userId || !programId) return false;
    try {
        const docId = `${userId}_${programId}`;
        await deleteDoc(doc(db, 'programWaitlists', docId));
        return true;
    } catch (e) { console.error('Error leaving program waitlist:', e); return false; }
}

// ── Check if user is on a program's waitlist ──
export async function isUserOnProgramWaitlist(userId, programId) {
    if (!firebaseEnabled || !db || !userId || !programId) return false;
    try {
        const docId = `${userId}_${programId}`;
        const snap = await getDoc(doc(db, 'programWaitlists', docId));
        return snap.exists();
    } catch (e) { return false; }
}

// ── Get all waitlist entries for a specific program (admin) ──
export async function getProgramWaitlistUsers(programId) {
    if (!firebaseEnabled || !db || !programId) return [];
    try {
        const q = query(
            collection(db, 'programWaitlists'),
            where('programId', '==', programId),
            orderBy('joinedAt', 'asc')
        );
        const snap = await getDocs(q);
        return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    } catch (e) { console.error('Error fetching program waitlist:', e); return []; }
}

// ── Get all waitlist entries across all programs (admin overview) ──
export async function getAllProgramWaitlists() {
    if (!firebaseEnabled || !db) return [];
    try {
        const q = query(collection(db, 'programWaitlists'), orderBy('joinedAt', 'asc'));
        const snap = await getDocs(q);
        return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    } catch (e) { console.error('Error fetching all waitlists:', e); return []; }
}

// ── Remove a user from a program waitlist (called server-side after purchase) ──
// This is a server-side helper — exported for use in API routes
export async function removeFromProgramWaitlist(userId, programId) {
    if (!firebaseEnabled || !db || !userId || !programId) return false;
    try {
        const docId = `${userId}_${programId}`;
        await deleteDoc(doc(db, 'programWaitlists', docId));
        return true;
    } catch (e) { console.error('Error removing from waitlist:', e); return false; }
}
